import { Palette, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  LP_BUTTON_FILLS,
  LP_BUTTON_SHAPES,
  LP_SPACING_SCALES,
  LP_THEME_FONTS,
  type LpTheme,
} from "@shared/lp-express-sections";

export const LP_FONT_LABELS: Record<string, string> = {
  inter: "Inter",
  system: "System",
  poppins: "Poppins",
  montserrat: "Montserrat",
  georgia: "Georgia",
  playfair: "Playfair Display",
  merriweather: "Merriweather",
  mono: "Monospace",
};

// Global theme controls for the whole landing page. Every field is optional —
// an absent field means "use the AI-generated default", so the Reset button
// just clears the theme object. Writes go through onSet/onReset which fire the
// pathless setTheme/resetTheme content ops (optimistic merge + persist).
export function LpThemePanel({
  theme,
  onSet,
  onReset,
}: {
  theme: LpTheme;
  onSet: (patch: Partial<LpTheme>) => void;
  onReset: () => void;
}) {
  const hasOverrides = Object.keys(theme).length > 0;
  const segBtn = (active: boolean) =>
    `h-7 px-2.5 text-xs capitalize rounded-md border transition-colors ${
      active
        ? "border-primary bg-primary/10 text-primary font-medium"
        : "border-border/60 text-muted-foreground hover:bg-muted"
    }`;
  return (
    <div className="rounded-lg border border-border/40 p-3" data-testid="card-theme-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1" data-testid="header-theme-panel">
          <Palette className="h-4 w-4 text-primary shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-tight">
              Page theme
              {hasOverrides && (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary align-middle">
                  Customized
                </span>
              )}
            </h3>
            <p className="text-xs text-muted-foreground">
              Fonts, colors, button style, and spacing for the whole page. Updates the preview instantly — republish to push changes to your live page.
            </p>
          </div>
        </div>
        {hasOverrides && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs shrink-0"
            onClick={onReset}
            data-testid="button-reset-theme"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset theme
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mt-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Heading font</span>
          <select
            className="h-9 rounded-md border border-border/60 bg-background px-2 text-sm"
            value={theme.headingFont ?? ""}
            onChange={(e) => onSet({ headingFont: e.target.value || undefined })}
            data-testid="select-theme-heading-font"
          >
            <option value="">Default</option>
            {Object.keys(LP_THEME_FONTS).map((k) => (
              <option key={k} value={k}>{LP_FONT_LABELS[k] ?? k}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Body font</span>
          <select
            className="h-9 rounded-md border border-border/60 bg-background px-2 text-sm"
            value={theme.bodyFont ?? ""}
            onChange={(e) => onSet({ bodyFont: e.target.value || undefined })}
            data-testid="select-theme-body-font"
          >
            <option value="">Default</option>
            {Object.keys(LP_THEME_FONTS).map((k) => (
              <option key={k} value={k}>{LP_FONT_LABELS[k] ?? k}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Primary color</span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-9 w-12 rounded-md border border-border/60 bg-background p-0.5 cursor-pointer"
              value={theme.primaryColor ?? "#1e293b"}
              onChange={(e) => onSet({ primaryColor: e.target.value })}
              data-testid="input-theme-primary-color"
            />
            {theme.primaryColor && (
              <button
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => onSet({ primaryColor: undefined })}
                data-testid="button-clear-theme-primary-color"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Accent color</span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-9 w-12 rounded-md border border-border/60 bg-background p-0.5 cursor-pointer"
              value={theme.accentColor ?? "#f97316"}
              onChange={(e) => onSet({ accentColor: e.target.value })}
              data-testid="input-theme-accent-color"
            />
            {theme.accentColor && (
              <button
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => onSet({ accentColor: undefined })}
                data-testid="button-clear-theme-accent-color"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Button shape</span>
          <div className="flex flex-wrap gap-1.5">
            {LP_BUTTON_SHAPES.map((s) => (
              <button
                key={s}
                className={segBtn(theme.buttonShape === s)}
                onClick={() => onSet({ buttonShape: theme.buttonShape === s ? undefined : s })}
                data-testid={`button-theme-shape-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Button fill</span>
          <div className="flex flex-wrap gap-1.5">
            {LP_BUTTON_FILLS.map((f) => (
              <button
                key={f}
                className={segBtn(theme.buttonFill === f)}
                onClick={() => onSet({ buttonFill: theme.buttonFill === f ? undefined : f })}
                data-testid={`button-theme-fill-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Section spacing</span>
          <div className="flex flex-wrap gap-1.5">
            {LP_SPACING_SCALES.map((sp) => (
              <button
                key={sp}
                className={segBtn(theme.spacing === sp)}
                onClick={() => onSet({ spacing: theme.spacing === sp ? undefined : sp })}
                data-testid={`button-theme-spacing-${sp}`}
              >
                {sp}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}